import { jwtDecode } from 'jwt-decode'
import {
  Cut,
  CutTag,
  DynamoFile,
  EPlusFolder,
  FileCommentTopic,
  FileStructure,
  OfficeRuntimeAccessToken,
  OfficeTokenCookie,
  TestDataOcr,
} from '../types'
import { isOfficeRuntimeToken } from './guards'
import { fetchPDFBatchFromLocal, fetchPDFFromLocal } from '../workbook'
import { getFileBatchV2, getFileV2 } from '../api'
import numeral from 'numeral'
import TextcutRectangleBuilder from './TextcutRectangleBuilder'
import { LRUCache } from 'lru-cache'
import mittEmitter from './mitt'

export const lruCache = new LRUCache<string, string>({
  max: 1,
  ttl: 1000 * 60 * 30, // 30 minutes
})

export const fileContainsComment = (
  file: DynamoFile,
  comments: FileCommentTopic[]
) => comments.some((topic) => topic.fileId === file.fileId)

export const publishOnReferenceSelectedEvent = (bindingId: string) => {
  mittEmitter.emit('ON_REFERENCE_SELECTED', bindingId)
}

export const getOfficeErrorTitle = (message: string) => {
  if (message.startsWith('Excel is in cell-editing mode'))
    return 'Excel is in cell-editing mode'
  return message
}

export const cacheFunc = async (
  key: string,
  func: () => Promise<string>
): Promise<string> => {
  let cachedValue = lruCache.get(key)
  if (cachedValue) {
    return cachedValue
  }
  try {
    const value = await func()
    lruCache.set(key, value)
    return value
  } catch (error) {
    console.error(`Error fetching data for key ${key}:`, error)
    throw error
  }
}

export const getOfficeRuntimeAccessToken =
  async (): Promise<OfficeRuntimeAccessToken> => {
    let token: string | undefined
    token = lruCache.get(OfficeTokenCookie.KEY)
    if (token) {
      console.log('Cached data found')
    }
    if (!token) {
      token = await cacheFunc(OfficeTokenCookie.KEY, () => {
        return Office.auth.getAccessToken({
          allowConsentPrompt: true,
          allowSignInPrompt: true,
        })
      })
      if (!token) throw new Error('Cannot get access token')
    }
    const decoded = jwtDecode(token)
    if (!isOfficeRuntimeToken(decoded))
      throw new Error('Internal Error: access denied')
    return {
      name: decoded.name,
      preferred_username: decoded.preferred_username,
      oid: decoded.oid,
    }
  }

export const publishBindingOnDataChangeEvent = (bindingId: string) => {
  mittEmitter.emit('BINDING_ON_DATA_CHANGE', bindingId)
}

export const getFileAndOcr = async (
  fileId: string,
  isLocalMode: boolean
): Promise<[string | Blob, TestDataOcr, string[]]> => {
  if (isLocalMode) {
    const f = await fetchPDFFromLocal(fileId)
    if (!f?.pdfBlob || !f.ocr)
      throw new Error(`Cannot fetch file ${fileId} from local`)
    return [f.pdfBlob, f.ocr, []]
  } else {
    const file = await getFileV2(fileId)
    return [file.pdf, file.ocr, file.ocrDateCache]
  }
}

export const getFileAndOcrBatch = async (
  fileIds: string[],
  isLocalMode: boolean
): Promise<[string, string | Blob, TestDataOcr, string[]][]> => {
  if (isLocalMode) {
    const files = await fetchPDFBatchFromLocal(fileIds)

    for (const f of files) {
      if (!f?.pdfBlob || !f.ocr)
        throw new Error(`Cannot fetch file ${f.fileId} from local`)
    }
    return files.map((f) => [f.fileId, f.pdfBlob, f.ocr, []])
  } else {
    const files = await getFileBatchV2(fileIds)
    return files.map((file) => [file.id, file.pdf, file.ocr, file.ocrDateCache])
  }
}

/**
 * Extracts the file name without the extension from a given file path.
 * @param filePath - The full path of the file.
 * @returns The file name without the extension.
 */
export const getFileName = (filePath: string): string => {
  const parts = filePath.split('/')
  const fullFileName = parts[parts.length - 1]
  const fileNameWithoutExtension = fullFileName
    .split('.')
    .slice(0, -1)
    .join('.')
  return fileNameWithoutExtension
}

export const fileSizeLessThan5MB = (size: number) =>
  size < numeral('5MB').value()!

export const getFileStructure = (
  folders: EPlusFolder[],
  files: { id: string; parentFolderId: string | undefined }[]
) => {
  const root: FileStructure = {}
  const folderMap = new Map<string, EPlusFolder>()
  const fileMap = new Map<string, typeof files>()
  files.forEach((file) => {
    if (!file.parentFolderId) {
      root[file.id] = null
    } else {
      if (fileMap.has(file.parentFolderId)) {
        fileMap.get(file.parentFolderId)!.push(file)
      } else {
        fileMap.set(file.parentFolderId, [file])
      }
    }
  })
  for (const folder of folders) {
    folderMap.set(folder.fileId, folder)
  }

  const parseFolder = (folder: EPlusFolder) => {
    let f = folder
    const parents: string[] = [folder.fileId]
    while (f.parentFolderId) {
      parents.push(f.parentFolderId)
      const tmp = folderMap.get(f.parentFolderId)
      if (!tmp) break
      f = tmp
    }
    parents.reverse()
    let r = root
    for (const p of parents) {
      if (!(p in r)) {
        r[p] = {}
      }
      r = r[p]!
    }
    const arr = fileMap.get(folder.fileId) ?? []
    for (const f of arr) {
      r[f.id] = null
    }
  }

  for (const folder of folders) {
    parseFolder(folder)
  }
  return root
}

export const createTextcutRectangle = ({
  fileId,
  filePage,
  h,
  range,
  sheetId,
  sheetName,
  top,
  w,
  x,
  y,
  rectId,
  rotationDegree,
  ocrH,
  ocrW,
  value,
  tag,
  fill,
  stroke,
  cellColor,
}: {
  range: Excel.Range
  fileId: string
  filePage: number
  sheetId: string
  sheetName: string
  top: number
  x: number
  y: number
  h: number
  w: number
  rectId: string
  rotationDegree: number
  ocrH: number
  ocrW: number
  value: any
  tag: CutTag
  stroke: string
  fill: string
  cellColor: string
}) => {
  const [, rangeAddr] = range.address.split('!')
  const elem = new TextcutRectangleBuilder()
    .addBindingId(rectId)
    .addBoudingInfo({
      x,
      y,
      h,
      w,
    })
    .addFileInfo({ fileId, filePage })
    .addSheetInfo({
      sheetId,
      sheetName,
      range: rangeAddr,
    })
    .addOcrInfo({
      id: '',
      height: rotationDegree === 0 || rotationDegree === 180 ? ocrH : ocrW,
      width: rotationDegree === 0 || rotationDegree === 180 ? ocrW : ocrH,
    })
    .addTag(tag)
    .addStyle({ cellColor, stroke, fill })
    .addValues([[value]])
    .addDegee(rotationDegree)
    .addTop(top)
    .build()

  return elem
}

export const getCutTypeStyle = (
  type: Cut
): { stroke: string; fill: string } => {
  if (type === Cut.SUM) {
    return {
      stroke: 'rgba(156, 87, 0)',
      fill: 'rgba(253,230,182,0.3)',
    }
  } else if (type === Cut.TEXTCUT) {
    return {
      stroke: 'rgba(30, 90,160)',
      fill: 'rgba(182,251,251, 0.3)',
    }
  } else if (type === Cut.TABLECUT) {
    return {
      stroke: 'green',
      fill: '',
    }
  } else if (type === Cut.VALIDATE) {
    return {
      stroke: 'rgba(0, 115, 78)',
      fill: 'rgba(204, 235, 224, 0.3)',
    }
  } else if (type === Cut.EXCEPTION) {
    return {
      stroke: 'rgba(156, 0, 6)',
      fill: 'rgba(251, 208, 215, 0.3)',
    }
  } else {
    return {
      stroke: 'rgb(0,0,0)',
      fill: 'rgb(0,0,0)',
    }
  }
}

export const getDialogSize = (
  height: number = 1024,
  width: number = 1366
): { height: number; width: number } => {
  return {
    height: (height / window.screen.height) * 100,
    width: (width / window.screen.width) * 100,
  }
}
